import { useRouter } from '@application/utils';
import type { RelatedLinksCategory, RelatedLinksData } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { isEmpty } from 'lodash/fp';
import { useMemo } from 'react';
import { useGetArticles } from './useGetArticles';
import { useGetBreeds } from './useGetBreeds';
import { useGetShelters } from './useGetShelters';

export const useRelatedLinks = (shelterId: string) => {
  const { routeLocationDescriptor } = useRouter();
  const { translate } = useTranslation();
  const { shelters, sheltersLoading } = useGetShelters(shelterId);
  const { breeds, breedsLoading } = useGetBreeds();
  const { articles, articlesLoading } = useGetArticles();

  const formattedShelters = useMemo((): RelatedLinksCategory | undefined => {
    if (sheltersLoading) return;
    if (!shelters?.length) return;

    return {
      label: translate('common.sections.relatedLinks.shelters.title'),
      items: shelters.map((x) => ({
        label: translate('common.sections.relatedLinks.shelters.link').replace(
          '$name',
          x.name ?? '',
        ),
        link: routeLocationDescriptor('shelter', { params: [x.translations[0]?.url ?? ''] }),
      })),
    };
  }, [routeLocationDescriptor, shelters, sheltersLoading, translate]);

  const formattedArticles = useMemo((): RelatedLinksCategory | undefined => {
    if (articlesLoading) return;
    if (!articles?.length) return;
    const foundArticles = articles.filter(
      (x) => x.translations?.[0]?.title && x.translations?.[0]?.url,
    );
    if (!foundArticles.length) return;
    return {
      label: translate('common.sections.relatedLinks.articles.title'),
      items: foundArticles.map((x) => ({
        label: x.translations[0].title,
        link: routeLocationDescriptor('article', { params: [x.translations[0].url] }),
      })),
    };
  }, [articles, articlesLoading, routeLocationDescriptor, translate]);

  const formattedBreeds = useMemo((): RelatedLinksCategory | undefined => {
    if (breedsLoading) return;
    if (!breeds.length) return;
    const foundBreeeds = breeds.filter(
      (x) => x.translations?.[0]?.name && x.translations?.[0]?.url,
    );
    if (!foundBreeeds.length) return;
    return {
      label: translate('common.sections.relatedLinks.breeds.titleDefault'),
      items: foundBreeeds.map((x) => ({
        label: translate('common.sections.relatedLinks.breeds.link').replace(
          '$breed',
          x.translations[0].name as string,
        ),
        link: routeLocationDescriptor('breed', { params: [x.translations[0].url as string] }),
      })),
    };
  }, [breeds, breedsLoading, routeLocationDescriptor, translate]);

  const relatedLinks = useMemo((): RelatedLinksData | undefined => {
    const links = {
      categories: [
        ...(formattedShelters ? [formattedShelters] : []),
        ...(formattedBreeds ? [formattedBreeds] : []),
        ...(formattedArticles ? [formattedArticles] : []),
      ].filter((x) => !!x),
    };
    if (isEmpty(links.categories)) return;
    return links;
  }, [formattedArticles, formattedBreeds, formattedShelters]);

  return { relatedLinks };
};
